import { graphqlOperation } from "@aws-amplify/api";
import secureRequest from "@toothfairy/shared-api/secureAPI";
import client from "@toothfairy/shared-api/src/Common/client";
import AppAuth from "@toothfairy/shared-ui/AppAuthScreen";
import envConfig from "../../../envConfig";
import * as mutations from "../../graphql/mutations";
import * as queries from "../../graphql/queries";
import { makeGraphQLRequest } from "..";

// Workspace uses Lambda authorization for workspace validation
// All Workspace queries go through the Lambda authorizer

const getWorkspacesByUserID = async (userID) => {
  const allItems = [];
  let nextToken = null;

  do {
    const workspacesAvailable = await makeGraphQLRequest(
      graphqlOperation(queries.workspacesByUser, {
        userID,
        limit: 100,
        nextToken,
      }),
    );

    const items = workspacesAvailable?.data?.workspacesByUser?.items || [];
    allItems.push(...items);
    nextToken = workspacesAvailable?.data?.workspacesByUser?.nextToken;
  } while (nextToken);

  const activeItems = allItems?.filter((u) => u?._deleted != true);

  if (activeItems.length === 0) {
    return [];
  }

  // Fetch every workspace detail in parallel — one round-trip total instead of
  // sequential batches. Each call fails soft (null) so one bad record can't
  // block the rest.
  const results = await Promise.all(
    activeItems.map((u) =>
      makeGraphQLRequest(
        graphqlOperation(queries.getWorkspace, { id: u?.workspaceID }),
      ).catch((error) => {
        console.warn(
          `Could not fetch workspace ${u?.workspaceID}:`,
          error.message,
        );
        return null;
      }),
    ),
  );

  return results.filter((result) => result !== null);
};

const createWorkspace = async (object) => {
  try {
    const response = await makeGraphQLRequest(
      graphqlOperation(mutations.createWorkspace, { input: object }),
    );
    return response.data.createWorkspace;
  } catch (error) {
    console.error("Error creating Workspace:", error);
    throw error;
  }
};

const updateWorkspace = async (id, args) => {
  try {
    // First, fetch the current version of the Workspace
    const getWorkspaceResponse = await makeGraphQLRequest(
      graphqlOperation(queries.getWorkspace, { id }),
    );
    const currentWorkspace = getWorkspaceResponse.data.getWorkspace;

    // Prepare the update input
    const updateInput = { id, _version: currentWorkspace._version };
    args.forEach((arg) => {
      updateInput[arg.property] = arg.value;
    });

    const response = await makeGraphQLRequest(
      graphqlOperation(mutations.updateWorkspace, { input: updateInput }),
    );
    return response.data.updateWorkspace;
  } catch (error) {
    console.error("Error updating Workspace:", error);
    throw error;
  }
};

const _deleteWorkspace = async (id) => {
  try {
    // First, fetch the current version of the Workspace
    const getWorkspaceResponse = await makeGraphQLRequest(
      graphqlOperation(queries.getWorkspace, { id }),
    );
    const currentWorkspace = getWorkspaceResponse.data.getWorkspace;

    const response = await makeGraphQLRequest(
      graphqlOperation(mutations.deleteWorkspace, {
        input: { id, _version: currentWorkspace._version },
      }),
    );
    return response.data.deleteWorkspace;
  } catch (error) {
    console.error("Error deleting Workspace:", error);
    throw error;
  }
};

const updateWorkspaceUsers = async ({ users, workspaceid }) => {
  const payload = await AppAuth.getCurrentSession();
  const url = `${envConfig.REACT_APP_REST_ENDPOINT}/users/update-workspace-users`;
  const body = {
    users,
    workspaceid,
  };
  return secureRequest(client, "POST", url, null, body, true, null, payload);
};

const getWorkspacesByAutoInviteDomain = async (email) => {
  const payload = await AppAuth.getCurrentSession();
  const url = `${envConfig.REACT_APP_REST_ENDPOINT}/users/workspaces-by-domain`;
  const params = { email };
  return secureRequest(client, "GET", url, params, null, true, null, payload);
};

const getWorkspaceByID = async (id) => {
  try {
    const response = await makeGraphQLRequest(
      graphqlOperation(queries.getWorkspace, { id }),
    );
    return response.data.getWorkspace;
  } catch (error) {
    console.error("Error fetching Workspace:", error);
    throw error;
  }
};

const rotateApiKey = async (workspaceId, currentApiKey) => {
  const url = `${envConfig.REACT_APP_REST_ENDPOINT}/apikeymanager/rotateApiKey?workspaceid=${workspaceId}`;
  console.log("API KEY", currentApiKey);
  const headers = { "x-api-key": currentApiKey };
  return secureRequest(client, "POST", url, null, {}, false, headers);
};

export default {
  createWorkspace,
  updateWorkspace,
  deleteWorkspace: _deleteWorkspace,
  updateWorkspaceUsers,
  getWorkspacesByUserID,
  getWorkspacesByAutoInviteDomain,
  getWorkspaceByID,
  rotateApiKey,
};
